import { INTERVAL_PERIODS } from "./syncFormat";

// Schedules are written as the FULL object (PUT is not a patch), so editing starts from the stored schedule and only
// the edited fields change; everything else, including the run counters, goes back exactly as it was.

export const DEDUP_PERIODS = ["hours", "days", "weeks", "months"];

export const emptyScheduleForm = () => ({
  name: "",
  task: "",
  description: "",
  schedule_type: "interval",
  every: "30",
  period: "minutes",
  minute: "0",
  hour: "*",
  day_of_month: "*",
  month_of_year: "*",
  day_of_week: "*",
  queue: "",
  argsText: "[]",
  kwargsText: "{}",
  dedupEnabled: false,
  dedupWindow: "1",
  dedupPeriod: "days",
  dedupTaskKey: "",
  enabled: true,
});

export const scheduleToForm = (schedule) => {
  const form = emptyScheduleForm();
  form.name = schedule.name || "";
  form.task = schedule.task || "";
  form.description = schedule.description || "";
  form.schedule_type = schedule.schedule_type === "crontab" ? "crontab" : "interval";
  if (schedule.interval) {
    form.every = String(schedule.interval.every ?? "");
    form.period = schedule.interval.period || "minutes";
  }
  if (schedule.crontab) {
    ["minute", "hour", "day_of_month", "month_of_year", "day_of_week"].forEach((key) => {
      form[key] = String(schedule.crontab[key] ?? "*");
    });
  }
  form.queue = schedule.queue || "";
  form.argsText = JSON.stringify(schedule.args ?? [], null, 2);
  form.kwargsText = JSON.stringify(schedule.kwargs ?? {}, null, 2);
  if (schedule.deduplication) {
    form.dedupEnabled = Boolean(schedule.deduplication.enabled);
    form.dedupWindow = String(schedule.deduplication.window ?? 1);
    form.dedupPeriod = schedule.deduplication.period || "days";
    form.dedupTaskKey = schedule.deduplication.task_key || "";
  }
  form.enabled = schedule.enabled !== false;
  return form;
};

const parseJson = (text, kind) => {
  try {
    const value = JSON.parse(text.trim() === "" ? (kind === "array" ? "[]" : "{}") : text);
    if (kind === "array" ? !Array.isArray(value) : value === null || typeof value !== "object" || Array.isArray(value)) {
      return { error: kind === "array" ? "Must be a JSON array, e.g. []" : "Must be a JSON object, e.g. {}" };
    }
    return { value };
  } catch (e) {
    return { error: "Not valid JSON." };
  }
};

export const validateScheduleForm = (form, { isCreate, availableTasks }) => {
  const errors = {};
  if (isCreate && !form.name.trim()) errors.name = "A name is required.";
  if (!form.task.trim()) errors.task = "Pick a task.";
  else if (isCreate && availableTasks?.length && !availableTasks.includes(form.task)) errors.task = "That task isn't registered.";
  if (form.schedule_type === "interval") {
    const every = Number(form.every);
    if (!Number.isInteger(every) || every <= 0) errors.every = "Enter a whole number above 0.";
    if (!INTERVAL_PERIODS.includes(form.period)) errors.period = "Pick a period.";
  } else {
    ["minute", "hour", "day_of_month", "month_of_year", "day_of_week"].forEach((key) => {
      if (!String(form[key]).trim()) errors[key] = "Required (use * for every).";
      else if (/\s/.test(String(form[key]).trim())) errors[key] = "No spaces.";
    });
  }
  const args = parseJson(form.argsText, "array");
  if (args.error) errors.argsText = args.error;
  const kwargs = parseJson(form.kwargsText, "object");
  if (kwargs.error) errors.kwargsText = kwargs.error;
  else if ("queue" in kwargs.value) errors.kwargsText = "The queue is set above, not in the keyword arguments.";
  if (form.dedupEnabled) {
    const window = Number(form.dedupWindow);
    if (!Number.isInteger(window) || window <= 0) errors.dedupWindow = "Enter a whole number above 0.";
  }
  return errors;
};

// The JSON body. `original` is the stored schedule when editing (its untouched fields are kept), or undefined when
// creating (the server fills metadata in). The timing object that doesn't match schedule_type is cleared.
// Read-only values the service adds to a schedule when it is read. They describe it, they are not part of it, so they
// are never sent back.
const DERIVED_FIELDS = ["next_run_at", "next_run_is_estimate", "next_run_unavailable_reason", "last_task_run", "last_task_run_attribution"];

export const formToBody = (form, original) => {
  const base = original ? { ...original } : {};
  DERIVED_FIELDS.forEach((key) => delete base[key]);
  const body = {
    ...base,
    name: original ? original.name : form.name.trim(),
    task: form.task.trim(),
    description: form.description.trim() || null,
    schedule_type: form.schedule_type,
    crontab:
      form.schedule_type === "crontab"
        ? {
            minute: form.minute.trim(),
            hour: form.hour.trim(),
            day_of_month: form.day_of_month.trim(),
            month_of_year: form.month_of_year.trim(),
            day_of_week: form.day_of_week.trim(),
          }
        : null,
    interval: form.schedule_type === "interval" ? { every: Number(form.every), period: form.period } : null,
    timezone: "UTC",
    args: parseJson(form.argsText, "array").value,
    kwargs: parseJson(form.kwargsText, "object").value,
    queue: form.queue.trim() || null,
    enabled: form.enabled,
    deduplication: form.dedupEnabled
      ? { enabled: true, window: Number(form.dedupWindow), period: form.dedupPeriod, task_key: form.dedupTaskKey.trim() || null }
      : original?.deduplication
        ? { ...original.deduplication, enabled: false }
        : null,
  };
  return body;
};
